// ── Shared response envelope — STEP Product APIs (breaking change 2026-07-24) ──
// Every step-product endpoint now returns ONE envelope:
//   success: { ok: true,  data: { …former top-level fields… } }
//   error:   { ok: false, error: { code: "UPPER_SNAKE", message, details? } }
// The HTTP status now carries meaning (was often always-200) — read status AND ok.
// n8n may still wrap the body in a single-item array.
//
// Error codes in use: VALIDATION_ERROR (400), NOT_FOUND (404),
// VERSION_CONFLICT (409), STEP_AUTH_FAILED / STEP_UPLOAD_FAILED / STEP_REJECTED (502),
// PROMPT_NOT_CONFIGURED (404), EXTRACTION_FAILED + extraction codes (422).
// Full list in stepErrors.ts. Since the 2026-08-03 backend release every code is
// UPPER_SNAKE_CASE; readEnvelope normalises the casing anyway so a workflow left
// on the old lower_snake extraction codes still matches STEP_ERROR.

import { normaliseCode } from './stepErrors';

// Typed error carrying the parsed { code, message, details } — callers branch on
// `.code` (e.g. VERSION_CONFLICT) and read conflict data from `.details`.
export class StepApiError extends Error {
  status: number;
  code: string;
  details?: any;
  constructor(status: number, code: string, message: string, details?: any) {
    super(message);
    this.name = 'StepApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

const unArray = (body: any): any =>
  (Array.isArray(body) && body.length > 0 ? body[0] : body);

// Core: turn a (status, parsed body) pair into the success payload, or throw a
// StepApiError. Success carries the payload under `data`; the `?? root` fallback
// tolerates any endpoint mid-migration that hasn't wrapped its body yet.
export const readEnvelope = <T = any>(status: number, body: any): T => {
  const root = unArray(body);
  if (root && root.ok === false) {
    const e = root.error ?? {};
    throw new StepApiError(
      status,
      normaliseCode(e.code) || 'UNKNOWN',
      typeof e.message === 'string' ? e.message : `HTTP ${status}`,
      e.details,
    );
  }
  if (status >= 400) {
    throw new StepApiError(status, 'HTTP_ERROR', `HTTP ${status}`);
  }
  return (root && typeof root === 'object' && 'data' in root ? (root as any).data : root) as T;
};

// fetch() path — reads the body once, then unwraps.
export const unwrapEnvelope = async <T = any>(res: Response): Promise<T> => {
  let body: any = {};
  try { body = await res.json(); } catch { /* non-JSON / empty error body */ }
  return readEnvelope<T>(res.status, body);
};

// Transient-failure handling (retry/backoff on 502/503/504) lives in the shared
// http util so the other agent pages reuse it. Re-exported here so the STEP
// panels keep importing it alongside the envelope helpers.
export { fetchWithRetry, TRANSIENT_STATUS, TRANSIENT_MESSAGE } from '../../utils/http';
export type { RetryOpts } from '../../utils/http';
